/* ═══════════════════════════════════════════════════════
   BLADEVAULT SHOPIFY THEME — theme.js
   Shopify AJAX Cart API, Predictive Search,
   Filters, FAQ accordion, mobile nav
════════════════════════════════════════════════════════ */

'use strict';

/* ─── UTILITY ─────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* ═══════════════════════════════════════════════════════
   SHOPIFY AJAX CART API
   Uses /cart/add.js, /cart.js, /cart/update.js
════════════════════════════════════════════════════════ */
class ShopifyCart {
  static async getCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  }

  static async addItem(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  static async updateCart(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return res.json();
  }

  static async clearCart() {
    const res = await fetch('/cart/clear.js', { method: 'POST' });
    return res.json();
  }
}

/* ─── Cart count badge updater ─────────────────────────── */
async function refreshCartCount() {
  try {
    const cart = await ShopifyCart.getCart();
    $$('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  } catch (e) {
    console.error('Cart refresh error:', e);
  }
}

/* ─── Cart notification toast ──────────────────────────── */
function showCartNotification(message) {
  const notif = $('#cart-notification');
  if (!notif) return;
  $('#cart-notification-text').textContent = message;
  notif.setAttribute('aria-hidden', 'false');
  notif.classList.add('show');
  setTimeout(() => {
    notif.classList.remove('show');
    notif.setAttribute('aria-hidden', 'true');
  }, 2800);
}

/* ─── Add to Cart buttons ──────────────────────────────── */
function initAddToCartButtons() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    const productTitle = btn.dataset.productTitle || 'Item';
    if (!variantId) return;

    btn.classList.add('loading');
    btn.textContent = 'Adding…';

    try {
      await ShopifyCart.addItem(variantId);
      btn.classList.remove('loading');
      btn.classList.add('added');
      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        Added!`;
      showCartNotification(`${productTitle} added to cart`);
      /* Fetch cart, update badge, then open drawer with data already populated */
      const cart = await ShopifyCart.getCart();
      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { cart: cart } }));
      setTimeout(() => {
        btn.classList.remove('added');
        btn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          Add to Cart`;
      }, 2200);
    } catch (err) {
      btn.classList.remove('loading');
      btn.textContent = 'Unavailable';
      console.error('Add to cart error:', err.message);
    }
  });
}

/* ─── Product page ATC ─────────────────────────────────── */
function initProductPageAtc() {
  const atcForm = $('[data-product-form]');
  if (!atcForm) return;

  atcForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = atcForm.querySelector('[data-atc-btn]');
    const variantId = atcForm.querySelector('[name="id"]').value;
    const qtyInput = atcForm.querySelector('[data-qty-input]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Adding to Cart…';

    try {
      await ShopifyCart.addItem(variantId, qty);
      btn.textContent = '✓ Added!';
      showCartNotification('Item added to cart');
      refreshCartCount();
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }, 2000);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = err.message || 'Error — try again';
    }
  });

  // Quantity +/- buttons
  $$('[data-qty-minus], [data-qty-plus]').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('[data-product-form]').querySelector('[data-qty-input]');
      if (!input) return;
      let v = parseInt(input.value, 10) || 1;
      v = btn.dataset.qtyMinus !== undefined ? Math.max(1, v - 1) : v + 1;
      input.value = v;
    });
  });
}

/* ═══════════════════════════════════════════════════════
   SHOPIFY PREDICTIVE SEARCH API
   Uses /search/suggest.json
════════════════════════════════════════════════════════ */
class PredictiveSearch {
  constructor(input, dropdown) {
    this.input = input;
    this.dropdown = dropdown;
    this.timeout = null;
    this.bindEvents();
  }

  bindEvents() {
    this.input.addEventListener('input', () => {
      clearTimeout(this.timeout);
      const q = this.input.value.trim();
      if (q.length < 2) { this.close(); return; }
      this.timeout = setTimeout(() => this.fetch(q), 280);
    });

    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2) this.fetch(this.input.value.trim());
    });

    document.addEventListener('click', (e) => {
      if (!this.input.contains(e.target) && !this.dropdown.contains(e.target)) this.close();
    });

    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Enter') {
        e.preventDefault();
        window.location.href = `/search?q=${encodeURIComponent(this.input.value)}`;
      }
    });
  }

  async fetch(query) {
    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&fields=title,product_type,variants.title,body_html`
      );
      const data = await res.json();
      this.render(data.resources.results.products || []);
    } catch {
      this.close();
    }
  }

  render(products) {
    if (!products.length) { this.close(); return; }
    this.dropdown.innerHTML = products.map(p => {
      const img = p.featured_image?.url
        ? `<img src="${p.featured_image.url}&width=80" alt="${p.title}" loading="lazy" />`
        : `<div style="width:40px;height:40px;background:#eee;border-radius:4px"></div>`;
      const price = p.price ? `$${(p.price / 100).toFixed(2)}` : '';
      return `<a href="${p.url}" class="search-result-item">
        ${img}
        <div>
          <div class="search-result-title">${p.title}</div>
          <div class="search-result-price">${price}</div>
        </div>
      </a>`;
    }).join('');
    this.dropdown.classList.add('open');
    this.dropdown.style.display = 'block';
  }

  close() {
    this.dropdown.classList.remove('open');
    this.dropdown.style.display = 'none';
  }
}

function initSearch() {
  const input = $('#header-search-input');
  const dropdown = $('#search-results-dropdown');
  if (input && dropdown) new PredictiveSearch(input, dropdown);

  // Search form submit
  const form = $('[data-search-form]');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input?.value?.trim();
      if (q) window.location.href = `/search?q=${encodeURIComponent(q)}&type=product`;
    });
  }
}

/* ═══════════════════════════════════════════════════════
   CLIENT-SIDE COLLECTION FILTERING
   Works with Shopify's built-in URL-based filtering
   (?filter.p.m.custom.brand=benchmade etc.) or
   falls back to JS filtering for non-Plus stores.
════════════════════════════════════════════════════════ */
function initCollectionFilters() {
  const filterForm = $('[data-filter-form]');
  if (!filterForm) return;

  // Sort
  const sortSelect = $('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
    // Reflect current sort in select
    const currentSort = new URL(window.location.href).searchParams.get('sort_by');
    if (currentSort) sortSelect.value = currentSort;
  }

  // Filter checkboxes (URL-based for Shopify)
  $$('[data-filter-input]', filterForm).forEach(input => {
    input.addEventListener('change', () => {
      const url = new URL(window.location.href);
      // Clear page param on filter change
      url.searchParams.delete('page');
      // Build filter params from all checked boxes
      url.searchParams.delete('filter.p.tag');
      $$('[data-filter-input]:checked', filterForm).forEach(checked => {
        url.searchParams.append(checked.dataset.filterParam || 'filter.p.tag', checked.value);
      });
      window.location.href = url.toString();
    });
  });

  // Reflect URL params in checkboxes
  const urlParams = new URL(window.location.href).searchParams;
  $$('[data-filter-input]', filterForm).forEach(input => {
    const param = input.dataset.filterParam || 'filter.p.tag';
    const values = urlParams.getAll(param);
    if (values.includes(input.value)) input.checked = true;
  });

  // Clear filters button
  const clearBtn = $('[data-filter-clear]');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      const url = new URL(window.location.href);
      // Remove all filter params
      [...url.searchParams.keys()].filter(k => k.startsWith('filter')).forEach(k => url.searchParams.delete(k));
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  // Price range filter
  const priceMin = $('[data-price-min]');
  const priceMax = $('[data-price-max]');
  const priceBtn = $('[data-price-apply]');
  if (priceBtn && priceMin && priceMax) {
    priceBtn.addEventListener('click', () => {
      const url = new URL(window.location.href);
      if (priceMin.value) url.searchParams.set('filter.v.price.gte', (parseFloat(priceMin.value) * 100).toString());
      if (priceMax.value) url.searchParams.set('filter.v.price.lte', (parseFloat(priceMax.value) * 100).toString());
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
    // Reflect URL price values
    const priceGte = urlParams.get('filter.v.price.gte');
    const priceLte = urlParams.get('filter.v.price.lte');
    if (priceGte) priceMin.value = (parseFloat(priceGte) / 100).toFixed(0);
    if (priceLte) priceMax.value = (parseFloat(priceLte) / 100).toFixed(0);
  }

  // Active filter tags (remove individual filter)
  $$('[data-remove-filter]').forEach(tag => {
    tag.addEventListener('click', () => {
      const url = new URL(window.location.href);
      const param = tag.dataset.removeParam;
      const value = tag.dataset.removeValue;
      const existing = url.searchParams.getAll(param).filter(v => v !== value);
      url.searchParams.delete(param);
      existing.forEach(v => url.searchParams.append(param, v));
      window.location.href = url.toString();
    });
  });
}

/* ═══════════════════════════════════════════════════════
   FAQ ACCORDION
════════════════════════════════════════════════════════ */
function initFaq() {
  $$('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.faq-item');
      const isOpen = item.classList.contains('open');
      // Close all
      $$('.faq-item.open').forEach(el => {
        el.classList.remove('open');
        el.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
      });
      // Open clicked (if wasn't already open)
      if (!isOpen) {
        item.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MOBILE NAV
════════════════════════════════════════════════════════ */
function initMobileNav() {
  const toggle = $('[data-mobile-nav-toggle]');
  const nav = $('[data-primary-nav]');
  if (!toggle || !nav) return;

  toggle.addEventListener('click', () => {
    const isOpen = nav.classList.toggle('mobile-open');
    toggle.setAttribute('aria-expanded', isOpen.toString());
    toggle.setAttribute('aria-label', isOpen ? 'Close navigation' : 'Open navigation');
  });
}

/* ═══════════════════════════════════════════════════════
   PRODUCT GALLERY (product page thumbnail switcher)
════════════════════════════════════════════════════════ */
function initProductGallery() {
  const mainImg = $('[data-gallery-main-img]');
  if (!mainImg) return;

  $$('[data-gallery-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      $$('[data-gallery-thumb]').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      mainImg.src = thumb.dataset.galleryThumb;
      mainImg.alt = thumb.querySelector('img')?.alt || '';
    });
  });
}

/* ═══════════════════════════════════════════════════════
   VARIANT SELECTOR (product page)
════════════════════════════════════════════════════════ */
function initVariantSelector() {
  const variantSelect = $('[data-variant-select]');
  if (!variantSelect) return;

  variantSelect.addEventListener('change', () => {
    const option = variantSelect.options[variantSelect.selectedIndex];
    const variantId = option.value;
    const price = option.dataset.price;
    const available = option.dataset.available !== 'false';
    const imgSrc = option.dataset.image;

    // Update hidden variant ID input
    const hiddenId = $('[name="id"]');
    if (hiddenId) hiddenId.value = variantId;

    // Update price display
    const priceEl = $('[data-variant-price]');
    if (priceEl && price) priceEl.textContent = price;

    // Update ATC button
    const atcBtn = $('[data-atc-btn]');
    if (atcBtn) {
      atcBtn.disabled = !available;
      atcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
    }

    // Update gallery image
    if (imgSrc) {
      const mainImg = $('[data-gallery-main-img]');
      if (mainImg) mainImg.src = imgSrc;
    }

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  });
}

/* ═══════════════════════════════════════════════════════
   STICKY HEADER shrink on scroll
════════════════════════════════════════════════════════ */
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > 40);
  }, { passive: true });
}

/* ═══════════════════════════════════════════════════════
   INIT ALL
════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  refreshCartCount();
  initAddToCartButtons();
  initProductPageAtc();
  initSearch();
  initCollectionFilters();
  initFaq();
  initMobileNav();
  initProductGallery();
  initVariantSelector();
  initStickyHeader();
});